import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../components/atoms';
import { DeleteConfirmationModal } from '../../components/organisms';
import { FormField } from '../../components/molecues/FormField';
import { DurationHmsField } from '../../components/molecues/DurationHmsField';
import { useDocumentaryActions } from '../../hooks/useDocumentaries';
import { getLanguageService } from '../../service/ServiceRegistry';
import type { Language } from '../../types/api/language';
import type {
  DocumentaryUpdateRequest,
  DocumentaryDetailsResponse,
  DocumentaryType,
} from '../../types/api/documentary';
import {
  DOCUMENTARY_SUBTYPE_OPTIONS,
  isSubtypeApplicable,
} from '../../types/api/documentary';
import type { VideoVariant, VideoMultipartPresignResponse } from '../../types/api/movie';
import toast from 'react-hot-toast';
import { resolveAssetUrl } from '../../utils/url';
import { formatDateTimeForDisplay } from '../../types/api/documentary';

const DOCUMENTARY_TYPE_OPTIONS: { value: DocumentaryType; label: string }[] = [
  { value: 'DHARMA_SPEECH', label: 'Dharma Speech' },
  { value: 'ASTROLOGY', label: 'Astrology' },
  { value: 'INTERVIEW', label: 'Interview' },
  { value: 'LITERARY_SEMINAR', label: 'Literary Seminar' },
];

const DEFAULT_VIDEO_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB per chunk
const VIDEO_MIN_PART_SIZE = 5 * 1024 * 1024; // Minimum part size (except last part)

const VIDEO_QUALITY_OPTIONS = [
  { value: 'ULTRA_HD_4K', label: 'Ultra HD (4K)' },
  { value: 'FULL_HD_1080P', label: 'Full HD (1080p)' },
  { value: 'HD_720P', label: 'HD (720p)' },
  { value: 'SD_480P', label: 'SD (480p)' },
  { value: 'SD_360P', label: 'SD (360p)' },
  { value: 'SD_240P', label: 'SD (240p)' },
];

const VIDEO_TYPE_OPTIONS = [
  { value: 'FULL_MOVIE', label: 'Full' },
  { value: 'TRAILER', label: 'Trailer' },
  { value: 'TEASER', label: 'Teaser' },
  { value: 'CLIP', label: 'Clip' },
  { value: 'EXTRA', label: 'Extra' },
];

const getAdaptiveChunkSize = (fileSize: number): number => {
  const sizeInGB = fileSize / (1024 * 1024 * 1024);

  if (sizeInGB >= 20) return 256 * 1024 * 1024;
  if (sizeInGB >= 10) return 192 * 1024 * 1024;
  if (sizeInGB >= 5) return 128 * 1024 * 1024;
  if (sizeInGB >= 2) return 96 * 1024 * 1024;
  if (sizeInGB >= 1) return 64 * 1024 * 1024;
  if (sizeInGB >= 0.5) return 32 * 1024 * 1024;
  if (sizeInGB >= 0.25) return 16 * 1024 * 1024;

  return DEFAULT_VIDEO_CHUNK_SIZE;
};

/**
 * DocumentaryEditPage Component
 *
 * Page for editing documentary metadata and managing thumbnail.
 * Keeps existing JSON update flow for metadata and uses /thumbnail endpoint
 * only when replacing the image.
 */
export const DocumentaryEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { documentaryId } = useParams<{ documentaryId: string }>();
  const {
    updateDocumentary,
    getDocumentary,
    updateDocumentaryThumbnail,
    initiateVideoUpload,
    completeVideoUpload,
    regenerateMultipartPartUrls,
    deleteVideo,
  } = useDocumentaryActions();
  const languageService = getLanguageService();

  const [details, setDetails] = useState<DocumentaryDetailsResponse | null>(null);
  const [formData, setFormData] = useState<DocumentaryUpdateRequest>({
    title: '',
    description: '',
    languageId: '',
    documentaryType: 'DHARMA_SPEECH',
    documentarySubtype: null,
    releaseDate: '',
    topic: '',
    expert: '',
    duration: undefined,
    priority: undefined,
  });

  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Thumbnail replacement state
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isUpdatingThumbnail, setIsUpdatingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);

  // Video upload state (simplified, similar to movie flow)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoQuality, setVideoQuality] = useState<string>('FULL_HD_1080P');
  const [videoType, setVideoType] = useState<string>('FULL_MOVIE');
  const [makeDefaultVideo, setMakeDefaultVideo] = useState<boolean>(true);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'initiating' | 'uploading' | 'completing' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState<boolean>(false);
  const [isDeletingVideo, setIsDeletingVideo] = useState<boolean>(false);
  const [selectedVideoVariant, setSelectedVideoVariant] = useState<VideoVariant | null>(null);

  // Load documentary + options
  useEffect(() => {
    if (!documentaryId) {
      toast.error('Documentary ID is required');
      navigate('/pages/documentaries');
      return;
    }

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        const [docDetails, langsResponse] = await Promise.all([
          getDocumentary(documentaryId),
          languageService.getLanguages({ page: 1, size: 100 }),
        ]);

        setDetails(docDetails);

        const documentary = docDetails.documentary;

        setFormData({
          title: documentary.title,
          description: documentary.description,
          languageId: documentary.languageId,
          documentaryType: documentary.documentaryType,
          documentarySubtype: documentary.documentarySubtype || null,
          releaseDate: documentary.releaseDate,
          topic: documentary.topic,
          expert: documentary.expert,
          duration: documentary.duration ?? undefined,
          priority: documentary.priority ?? undefined,
          status: documentary.status,
          accessLevel: documentary.accessLevel,
        });

        setLanguages(langsResponse.items);
      } catch (error: any) {
        console.error('Failed to load documentary:', error);
        setLoadError(error.message || 'Failed to load documentary');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [documentaryId, navigate, getDocumentary, languageService]);

  const handleChange = (field: keyof DocumentaryUpdateRequest, value: any) => {
    // If documentaryType changes, clear subtype if not applicable
    if (field === 'documentaryType') {
      const newType = value as DocumentaryType;
      if (!isSubtypeApplicable(newType)) {
        // Clear subtype if changing to non-DHARMA_SPEECH type
        setFormData((prev) => ({ 
          ...prev, 
          [field]: value,
          documentarySubtype: null  // Clear subtype
        }));
        return;
      }
    }
    
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.languageId) {
      newErrors.languageId = 'Language is required';
    }
    if (!formData.documentaryType) {
      newErrors.documentaryType = 'Documentary type is required';
    }
    if (
      formData.documentaryType &&
      isSubtypeApplicable(formData.documentaryType) &&
      !formData.documentarySubtype
    ) {
      newErrors.documentarySubtype =
        'Subtype is required for Dharma Speech (determines mobile app section)';
    }
    if (formData.duration !== undefined && formData.duration < 1) {
      newErrors.duration = 'Duration must be at least 1 second when provided';
    }

    if (
      formData.priority !== undefined &&
      (!Number.isInteger(formData.priority) || formData.priority < 0)
    ) {
      newErrors.priority = 'Priority must be an integer >= 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normaliseEtag = (raw: string | null): string => {
    if (!raw) {
      throw new Error('Missing ETag value');
    }
    return raw.replace(/^W\//, '').replace(/^"+|"+$/g, '');
  };

  const resetVideoUploadState = () => {
    setVideoFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      resetVideoUploadState();
      return;
    }

    if (file.type && !file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      resetVideoUploadState();
      return;
    }

    if (file.size === 0) {
      setUploadError('Selected file is empty');
      resetVideoUploadState();
      return;
    }

    setVideoFile(file);
    setUploadError(null);
    setUploadProgress(0);
    setUploadStatus('idle');
  };

  const getVideoFileId = (variant: VideoVariant): string | null =>
    variant.videoId || variant.fileId || variant.key || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentaryId) return;
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await updateDocumentary(documentaryId, formData);
      toast.success('Documentary updated successfully');
      navigate('/pages/documentaries');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update documentary');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setThumbnailError(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setThumbnailError('Please select an image file');
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setThumbnailError('File size must be less than 10MB');
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }

    setThumbnailFile(file);
    setThumbnailError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleThumbnailUpdate = async () => {
    if (!documentaryId) {
      toast.error('Documentary ID is missing');
      return;
    }
    if (!thumbnailFile) {
      setThumbnailError('Please select an image to upload');
      return;
    }

    setIsUpdatingThumbnail(true);
    try {
      const updated = await updateDocumentaryThumbnail(documentaryId, thumbnailFile);
      // Update local details thumbnailUrl to avoid stale image
      setDetails((prev) =>
        prev
          ? {
              ...prev,
              documentary: {
                ...prev.documentary,
                thumbnailUrl: updated.thumbnailUrl,
              },
            }
          : prev
      );
      toast.success('Thumbnail updated successfully');
      setThumbnailFile(null);
      setThumbnailPreview(null);
    } catch (error: any) {
      console.error('Failed to update documentary thumbnail:', error);
      toast.error(error.message || 'Failed to update thumbnail');
    } finally {
      setIsUpdatingThumbnail(false);
    }
  };

  const handleVideoUpload = async () => {
    if (!documentaryId) {
      toast.error('Documentary ID is missing');
      return;
    }

    if (!videoFile) {
      setUploadError('Please select a video file to upload');
      return;
    }

    const fileSize = videoFile.size;
    const adaptiveChunkSize = Math.max(getAdaptiveChunkSize(fileSize), VIDEO_MIN_PART_SIZE);
    const partCount = Math.max(1, Math.ceil(fileSize / adaptiveChunkSize));

    setIsUploadingVideo(true);
    setUploadStatus('initiating');
    setUploadProgress(0);
    setUploadError(null);

    try {
      const initiateResponse = await initiateVideoUpload(documentaryId, {
        filename: videoFile.name,
        contentType: videoFile.type || 'application/octet-stream',
        partCount,
        quality: videoQuality,
        videoType,
        makeDefault: makeDefaultVideo,
      });

      // Get presigned URLs for all parts
      const partNumbers = Array.from({ length: partCount }, (_, idx) => idx + 1);
      const presignResponse: VideoMultipartPresignResponse =
        await regenerateMultipartPartUrls(documentaryId, {
          key: initiateResponse.key,
          uploadId: initiateResponse.uploadId,
          partNumbers,
        });

      if (!presignResponse.parts || presignResponse.parts.length === 0) {
        throw new Error('Failed to obtain presigned URLs for upload');
      }

      setUploadStatus('uploading');

      const completedParts: { partNumber: number; eTag: string }[] = [];
      let uploadedBytes = 0;

      const sortedParts = [...presignResponse.parts].sort(
        (a, b) => a.partNumber - b.partNumber
      );

      for (const part of sortedParts) {
        const start = (part.partNumber - 1) * adaptiveChunkSize;
        const end = Math.min(start + adaptiveChunkSize, videoFile.size);
        const chunk = videoFile.slice(start, end);

        const response = await fetch(part.url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: chunk,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload part ${part.partNumber} (status ${response.status})`);
        }

        const eTag = normaliseEtag(response.headers.get('ETag'));
        completedParts.push({ partNumber: part.partNumber, eTag });
        uploadedBytes += chunk.size;

        setUploadProgress(Math.min(100, Math.round((uploadedBytes / fileSize) * 100)));
      }

      setUploadStatus('completing');

      await completeVideoUpload(documentaryId, {
        key: initiateResponse.key,
        uploadId: initiateResponse.uploadId,
        parts: completedParts,
        quality: videoQuality,
        videoType,
        makeDefault: makeDefaultVideo,
      });

      toast.success('Video uploaded successfully');
      setUploadProgress(100);

      const refreshed = await getDocumentary(documentaryId);
      setDetails(refreshed);
      resetVideoUploadState();
    } catch (error: any) {
      console.error('Failed to upload video:', error);
      const msg = error?.message || 'Failed to upload video';
      setUploadError(msg);
      toast.error(msg);
      setUploadStatus('error');
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleDeleteVideo = (variant: VideoVariant) => {
    const fileId = getVideoFileId(variant);
    if (!fileId) {
      toast.error('Cannot delete video: Missing video identifier');
      return;
    }
    setSelectedVideoVariant(variant);
  };

  const handleDeleteVideoConfirm = async () => {
    if (!selectedVideoVariant || !documentaryId || !details) return;

    const fileId = getVideoFileId(selectedVideoVariant);
    if (!fileId) {
      toast.error('Cannot delete video: Missing video identifier');
      setSelectedVideoVariant(null);
      return;
    }

    setIsDeletingVideo(true);
    try {
      await deleteVideo(details.documentary.contentId, fileId);
      const refreshed = await getDocumentary(documentaryId);
      setDetails(refreshed);
      toast.success('Video deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete video:', error);
      toast.error(error?.message || 'Failed to delete video');
    } finally {
      setIsDeletingVideo(false);
      setSelectedVideoVariant(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!details || !details.documentary) {
    return (
      <div className="text-center py-12">
        {loadError ? (
          <>
            <p className="text-red-600 mb-4">{loadError}</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pages/documentaries')}
                className="mt-4"
              >
                Back to Documentaries
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-600">Documentary not found</p>
            <Button
              variant="outline"
              onClick={() => navigate('/pages/documentaries')}
              className="mt-4"
            >
              Back to Documentaries
            </Button>
          </>
        )}
      </div>
    );
  }

  const documentary = details.documentary;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Edit Documentary</h1>
          <p className="text-gray-600 mt-1">Update documentary information</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/pages/documentaries')}>
          Cancel
        </Button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6"
      >
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 pb-2 text-left">
            Basic Information
          </h2>

          <FormField
            label="Title"
            value={formData.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter documentary title"
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter documentary description"
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Language <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.languageId || ''}
                onChange={(e) => handleChange('languageId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="">Select a language</option>
                {languages
                  .filter((lang) => lang.isActive)
                  .map((lang) => (
                    <option key={lang.languageId} value={lang.languageId}>
                      {lang.name} ({lang.nameMm})
                    </option>
                  ))}
              </select>
              {errors.languageId && (
                <p className="text-sm text-red-600 mt-1.5">{errors.languageId}</p>
              )}
            </div>

            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Documentary Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.documentaryType || 'DHARMA_SPEECH'}
                onChange={(e) =>
                  handleChange('documentaryType', e.target.value as DocumentaryType)
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                {DOCUMENTARY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.documentaryType && (
                <p className="text-sm text-red-600 mt-1.5">{errors.documentaryType}</p>
              )}
            </div>
          </div>

          {/* Documentary Subtype - Required for DHARMA_SPEECH */}
          {isSubtypeApplicable(formData.documentaryType || details?.documentary.documentaryType || 'DHARMA_SPEECH') && (
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Documentary Subtype <span className="text-red-500">*</span>
              </label>

              {!formData.documentarySubtype && (
                <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left">
                  <svg
                    className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.987-1.742 2.987H3.481c-1.53 0-2.493-1.653-1.743-2.987L8.257 3.1zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-xs text-amber-800">
                    This documentary has no category set, so it does not appear under
                    either <strong>တရားတော်</strong> or <strong>သုတ္တန်</strong> tab
                    in the mobile app. Please select a category before saving.
                  </p>
                </div>
              )}

              <select
                value={formData.documentarySubtype || ''}
                onChange={(e) =>
                  handleChange('documentarySubtype', e.target.value || null)
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="" disabled>
                  Select subtype...
                </option>
                {DOCUMENTARY_SUBTYPE_OPTIONS.map((option) => (
                  <option key={option.value || ''} value={option.value || ''}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.documentarySubtype && (
                <p className="text-sm text-red-600 mt-1.5">{errors.documentarySubtype}</p>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Determines which section the documentary appears in on the mobile app
                (တရားတော် or သုတ္တန်).
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Release Date"
              type="date"
              value={formData.releaseDate || ''}
              onChange={(e) => handleChange('releaseDate', e.target.value)}
            />

            <FormField
              label="Priority"
              type="number"
              value={formData.priority != null ? String(formData.priority) : ''}
              onChange={(e) =>
                handleChange(
                  'priority',
                  e.target.value ? parseInt(e.target.value, 10) : undefined
                )
              }
              error={errors.priority}
              min={0}
              placeholder="0"
            />

            <DurationHmsField
              label="Duration"
              valueSeconds={formData.duration ?? undefined}
              onSecondsChange={(sec) => handleChange('duration', sec)}
              required={false}
              error={errors.duration}
              helperText="Optional. Sent as total seconds."
              idPrefix="doc-edit-duration"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Topic"
              value={formData.topic || ''}
              onChange={(e) => handleChange('topic', e.target.value)}
              placeholder="Enter main topic"
            />

            <FormField
              label="Expert / Speaker"
              value={formData.expert || ''}
              onChange={(e) => handleChange('expert', e.target.value)}
              placeholder="Enter expert or speaker name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Status
              </label>
              <select
                value={formData.status || documentary.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>

            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Access Level
              </label>
              <select
                value={formData.accessLevel || documentary.accessLevel}
                onChange={(e) => handleChange('accessLevel', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="FREE">Free</option>
                <option value="PREMIUM">Premium</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>
          </div>
        </div>

        {/* Current Thumbnail & Thumbnail Update */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-t border-dashed border-gray-600 pt-4 pb-2 text-left">
            Thumbnail
          </h2>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Current thumbnail */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700 text-left">Current Thumbnail</p>
              <div className="relative">
                {documentary.thumbnailUrl ? (
                  <img
                    src={resolveAssetUrl(documentary.thumbnailUrl)}
                    alt={documentary.title}
                    className="w-40 h-56 object-cover rounded-lg border border-gray-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="w-40 h-56 bg-gray-200 rounded-lg border border-gray-300 flex items-center justify-center"
                  style={{ display: documentary.thumbnailUrl ? 'none' : 'flex' }}
                >
                  <div className="text-center">
                    <svg
                      className="w-10 h-10 text-gray-400 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-xs text-gray-500">No Image</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-left">
                Uploaded at {formatDateTimeForDisplay(documentary.uploadDate)}
              </p>
            </div>

            {/* Thumbnail replace */}
            <div className="flex-1 space-y-3">
              <p className="text-sm font-medium text-gray-700 text-left">
                Replace Thumbnail (optional)
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailFileChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              />
              {thumbnailError && (
                <p className="text-sm text-red-600">{thumbnailError}</p>
              )}
              <p className="text-xs text-gray-500 text-left">
                JPEG, PNG, or WebP. Max 10MB. Uses dedicated /thumbnail endpoint without
                touching other metadata.
              </p>
              {thumbnailPreview && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 mb-1 text-left">New Thumbnail Preview</p>
                  <img
                    src={thumbnailPreview}
                    alt="New thumbnail preview"
                    className="w-32 h-44 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleThumbnailUpdate}
                  disabled={!thumbnailFile || isUpdatingThumbnail}
                  isLoading={isUpdatingThumbnail}
                >
                  Update Thumbnail Only
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Video Upload */}
        <div className="space-y-4">
          <h2 className="text-base text-left font-semibold text-gray-900 border-t border-dashed border-gray-600 pt-4 pb-2">
            Video Upload
          </h2>
          <p className="text-sm text-blue-500 text-left mb-2">
            Upload a video variant. Large files are uploaded in chunks using multipart upload.
          </p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-left text-gray-700">
                Video File <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoFileChange}
                disabled={isUploadingVideo}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              />
              <p className="text-xs text-gray-500">
                Supported: MP4, MOV, MKV, WEBM and other video formats. Minimum chunk size 5MB per part.
              </p>
              {videoFile && (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <p className="font-medium truncate">{videoFile.name}</p>
                  <p>
                    Size: {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-left text-gray-700 mb-3">
                    Quality <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={videoQuality}
                    onChange={(e) => setVideoQuality(e.target.value)}
                    disabled={isUploadingVideo}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
                  >
                    {VIDEO_QUALITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-left text-gray-700 mb-3">
                    Video Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={videoType}
                    onChange={(e) => setVideoType(e.target.value)}
                    disabled={isUploadingVideo}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
                  >
                    {VIDEO_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                  checked={makeDefaultVideo}
                  onChange={(e) => setMakeDefaultVideo(e.target.checked)}
                  disabled={isUploadingVideo}
                />
                Set as default playback source
              </label>

              {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

              {(isUploadingVideo || uploadProgress > 0) && (
                <div className="space-y-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-2 bg-[#EAB308] transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    {isUploadingVideo
                      ? uploadStatus === 'uploading'
                        ? `Uploading... ${uploadProgress}%`
                        : uploadStatus === 'initiating'
                        ? 'Preparing upload session...'
                        : uploadStatus === 'completing'
                        ? 'Finalising upload...'
                        : 'Processing...'
                      : uploadProgress === 100
                      ? 'Upload completed'
                      : `Progress: ${uploadProgress}%`}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleVideoUpload}
                  disabled={!videoFile || isUploadingVideo}
                  isLoading={isUploadingVideo}
                >
                  Upload Video
                </Button>
                {videoFile && !isUploadingVideo && (
                  <Button type="button" variant="outline" onClick={resetVideoUploadState}>
                    Clear Selection
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Existing Video Variants */}
        {details.videoVariants && details.videoVariants.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Existing Video Variants
            </h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Quality
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Default
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Updated
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {details.videoVariants.map((variant: VideoVariant) => {
                    const variantKey =
                      variant.fileId ||
                      variant.videoId ||
                      variant.key ||
                      `${variant.videoType}-${variant.quality}`;

                    const statusClasses =
                      variant.status === 'READY'
                        ? 'bg-green-100 text-green-800'
                        : variant.status === 'PROCESSING' || variant.status === 'UPLOADING'
                        ? 'bg-yellow-100 text-yellow-800'
                        : variant.status === 'FAILED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800';

                    return (
                      <tr key={variantKey}>
                        <td className="px-4 py-3 text-gray-800">{variant.videoType || '-'}</td>
                        <td className="px-4 py-3 text-gray-800">{variant.quality || '-'}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusClasses}`}
                          >
                            {variant.status || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {variant.isDefault ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                              Default
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {formatDateTimeForDisplay(variant.updatedAt || variant.createdAt || null)}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteVideo(variant)}
                            disabled={isDeletingVideo}
                            ariaLabel={`Delete ${variant.videoType || 'video'} - ${variant.quality || 'unknown quality'}`}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/pages/documentaries')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Update Documentary
          </Button>
        </div>
      </form>

      <DeleteConfirmationModal
        isOpen={!!selectedVideoVariant}
        onClose={() => setSelectedVideoVariant(null)}
        onConfirm={handleDeleteVideoConfirm}
        title="Delete Video"
        message="Are you sure you want to delete this video variant? This action cannot be undone."
      />
    </div>
  );
};

export default DocumentaryEditPage;

